/**
 * usePlatform — client platform detection.
 *
 * Detects the AstroKuberChat Capacitor shells (iOS + Android) using
 * the User-Agent marker we set in `capacitor.config.ts`.
 *
 *   iOS shell UA suffix:      "AstroKuberChat-iOS/1.0.0"
 *   Android shell UA suffix:  "AstroKuberChat/2.0.16"
 *
 * `hidePurchases` is the important one — Apple's App Store forbids
 * in-app UI that links out to external payment methods (Razorpay
 * checkout, wallet top-ups, "Buy report" CTAs). On iOS we render
 * a "Read-only mode" placeholder instead, keeping the app compliant
 * with Apple Guideline 3.1.3(a) ("Reader" apps).
 *
 * We deliberately do NOT hit the backend for this — the UA check is
 * cheap, runs before first paint, and cannot be spoofed to affect
 * revenue (a spoofer only hides their own purchase UI, no risk to us).
 */
import { useMemo } from "react";

function readPlatform() {
  if (typeof window === "undefined") {
    return { isIOSApp: false, isAndroidApp: false, isNativeApp: false, hidePurchases: false, platform: "web" };
  }
  const ua = String(window.navigator?.userAgent || "");
  const isIOSApp = /AstroKuberChat-iOS/i.test(ua);
  const isAndroidApp = /AstroKuberChat\/\d/i.test(ua) && /Android/i.test(ua);
  const isNativeApp = isIOSApp || isAndroidApp;
  return {
    isIOSApp,
    isAndroidApp,
    isNativeApp,
    // Apple = 0 payment UI. Android is unaffected — Razorpay is fine on Play.
    hidePurchases: isIOSApp,
    platform: isIOSApp ? "ios" : isAndroidApp ? "android" : "web",
  };
}

export default function usePlatform() {
  // The UA never changes during a session, so cache once via useMemo.
  return useMemo(readPlatform, []);
}

// Non-hook accessor for places that can't use hooks (e.g. api.js axios
// interceptor injecting the X-Client-Platform header on every request).
export function getPlatformSync() {
  return readPlatform();
}
